import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import { fetchUnvalidatedPeriodsRequest } from '../../actions/fetchUnvalidatedPeriodsActions';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import Option from '../../components/Option';
import PageLayout from '../../components/PageLayout';
import analytics from '../../lib/analytics';
import { fullDateFormat } from '../../lib/dateFormatter';
import { track } from '../../lib/heapHelpers';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { periodPropTypes } from '../../reducers/propTypes';
import {
  chosenCoursePeriodsSelector,
  periodSelector,
  periodsTimeStampSelector,
  chosenCourseSelector,
} from '../../reducers/selectors';

const PeriodRequestPage = ({
  periods,
  currentPeriod,
  courseGuid,
  dispatchFetchUnvalidatedPeriods,
  dispatchAddPageData,
  dispatchWizardNav,
  periodsTimeStamp,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (periods.length === 0 || periodsTimeStamp === null) {
        await dispatchFetchUnvalidatedPeriods(courseGuid);
      }
      setIsLoading(false);
      setSelectedPeriod(periods[0]);
    };

    fetchData();
  }, [periods, periodsTimeStamp, courseGuid, dispatchFetchUnvalidatedPeriods]);

  const handleSubmit = event => {
    event.preventDefault();

    analytics.sendEvent('Click - Next', 'period-request');

    const { periodGuid, currency, minLoanSize } = selectedPeriod;
    const futurePeriods = periods.filter(period => {
      return period.periodGuid !== periodGuid;
    });

    const periodPayload = payloadFactory.createAddPageDataPayload(
      'period',
      selectedPeriod,
      'coursePeriodGuid',
      periodGuid,
    );
    const futurePeriodsPayload = payloadFactory.createAddPageDataPayload(
      'futurePeriods',
      futurePeriods,
    );
    dispatchAddPageData(payloadFactory.createAddPageDataPayload('baseCurrency', currency));
    dispatchAddPageData(
      payloadFactory.createAddPageDataPayload(
        'periodStartDate',
        fullDateFormat(selectedPeriod.startDate),
      ),
    );
    dispatchAddPageData(payloadFactory.createAddPageDataPayload('courseMinLoanSize', minLoanSize));
    dispatchAddPageData(periodPayload);
    dispatchAddPageData(futurePeriodsPayload);
    track('Submit - Period Request', { period_input: selectedPeriod.periodName });
    dispatchWizardNav({ dir: 'fwd' });
  };

  const handleSelect = (index, period) => {
    setSelectedPeriod(period);
    setSelectedPeriodIndex(index);
  };

  const isPeriodDateInvalid = useMemo(() => {
    if (selectedPeriod) {
      const currentDate = new Date(Date.now());
      const endDate = selectedPeriod.endDate;
      const startDate = selectedPeriod.startDate;
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const day = currentDate.getDate();
      const nextYear = new Date(year + 1, month, day);
      if (endDate < currentDate) {
        setError('Selected period must not have already ended.');
        return true;
      }

      if (nextYear < startDate) {
        setError('Selected period must start in the next 12 months');
        return true;
      }

      setError(null);
      return false;
    }
  }, [selectedPeriod]);

  const shouldShowError = isPeriodDateInvalid;

  const renderError = () => {
    if (shouldShowError) {
      return <div className="row-spacer margin-top-bottom text-error">{error}</div>;
    }

    return null;
  };

  const helpText =
    'You’d need to apply separately for each academic period that you require funding';

  return (
    <div>
      {!isLoading && periods.length > 0 && (
        <PageLayout
          title="For which academic period of the programme are you requesting the funding for?"
          helpText={helpText}
        >
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col col--10/16-small col--12/16-medium">
                {periods.map((option, index) => {
                  return (
                    <Option
                      key={option.periodGuid}
                      index={index}
                      option={option}
                      selectedOption={selectedPeriod}
                      selectedIndex={selectedPeriodIndex}
                      handleClickEvent={() => handleSelect(index, option)}
                    />
                  );
                })}
              </div>
            </div>

            <div className="mt-10">
              <Button
                type="submit"
                className="niso-button"
                onClick={e => handleSubmit(e)}
                isDisabled={isPeriodDateInvalid}
              >
                Next
              </Button>
            </div>
            {renderError()}
          </form>
        </PageLayout>
      )}
      {isLoading && periods.length === 0 && (
        <section className="container container--space-y text-center">
          <Loading />
        </section>
      )}
    </div>
  );
};

PeriodRequestPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  periods: PropTypes.arrayOf(periodPropTypes).isRequired,
  periodsTimeStamp: PropTypes.string,
  dispatchFetchUnvalidatedPeriods: PropTypes.func.isRequired,
  courseGuid: PropTypes.string.isRequired,
  currentPeriod: PropTypes.shape({
    periodName: PropTypes.string,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

PeriodRequestPage.defaultProps = {
  periodsTimeStamp: null,
};

export const mapStateToProps = reduxStore => ({
  periods: chosenCoursePeriodsSelector(reduxStore),
  currentPeriod: periodSelector(reduxStore),
  deposit: reduxStore.builder.ui.deposit,
  livingExpenses: reduxStore.builder.ui.livingExpenses,
  tuitionCost: reduxStore.builder.ui.tuitionCost,
  periodsTimeStamp: periodsTimeStampSelector(reduxStore),
  courseGuid: chosenCourseSelector(reduxStore),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
  dispatchFetchUnvalidatedPeriods: fetchUnvalidatedPeriodsRequest,
};

export { PeriodRequestPage };

export default connect(mapStateToProps, mapDispatchToProps)(PeriodRequestPage);
